import type { Action, InputManager } from '../game/Input';

interface Btn {
  el: HTMLDivElement;
  action: Action;
}

/**
 * On-screen gamepad bar shown below the game canvas during gameplay (all
 * viewports, primarily for touch devices). Covers the full yknytt action set:
 * d-pad, jump/walk/umbrella/hologram, and info/map/pause. Wired to the
 * InputManager's touch overlay; main.ts shrinks the canvas to make room.
 *
 * Pointers are tracked per-cluster, not per-button, so it behaves like real
 * gamepad hardware: the d-pad is a radial zone (diagonals = two directions at
 * once, and the thumb can roll between directions without lifting), and a
 * finger can slide between face buttons. Each finger is tracked separately,
 * so pressing d-pad + jump simultaneously works.
 */
export class TouchControls {
  readonly el: HTMLDivElement;
  private buttons: Btn[] = [];
  private raf = 0;
  /** Actions currently held by each active pointer. */
  private pointers = new Map<number, Set<Action>>();
  /** Union of all pointers' actions currently forwarded to the InputManager. */
  private touched = new Set<Action>();

  constructor(
    private input: InputManager,
    parent: HTMLElement,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'touch-controls';
    this.el.innerHTML = `
      <div class="touch-dpad">
        <div class="tbtn t-up" data-action="up">▲</div>
        <div class="tbtn t-left" data-action="left">◀</div>
        <div class="tbtn t-right" data-action="right">▶</div>
        <div class="tbtn t-down" data-action="down">▼</div>
      </div>
      <div class="touch-menu">
        <div class="tbtn t-pill" data-action="show_info">info</div>
        <div class="tbtn t-pill" data-action="pause">pause</div>
        <div class="tbtn t-pill" data-action="map">map</div>
        <div class="tbtn t-pill" data-action="debug_die">reset</div>
      </div>
      <div class="touch-actions">
        <div class="tbtn t-face t-hologram" data-action="hologram">W<span class="tlabel">holo</span></div>
        <div class="tbtn t-face t-walk" data-action="walk">A<span class="tlabel">walk</span></div>
        <div class="tbtn t-face t-umbrella" data-action="umbrella">D<span class="tlabel">umbr</span></div>
        <div class="tbtn t-face t-jump" data-action="jump">S<span class="tlabel">jump</span></div>
      </div>`;
    parent.appendChild(this.el);

    for (const el of Array.from(this.el.querySelectorAll<HTMLDivElement>('.tbtn'))) {
      this.buttons.push({ el, action: el.dataset.action as Action });
    }

    const dpad = this.el.querySelector<HTMLDivElement>('.touch-dpad')!;
    this.trackCluster(dpad, (x, y) => this.dpadActions(dpad, x, y));
    for (const sel of ['.touch-actions', '.touch-menu']) {
      const cluster = this.el.querySelector<HTMLDivElement>(sel)!;
      this.trackCluster(cluster, (x, y) => this.buttonActions(cluster, x, y));
    }

    // iOS Safari can still double-tap-zoom in edge cases despite
    // touch-action; a non-passive touchend preventDefault is the hard stop.
    for (const ev of ['touchstart', 'touchmove', 'touchend'] as const) {
      this.el.addEventListener(ev, (e) => e.preventDefault(), { passive: false });
    }

    this.raf = requestAnimationFrame(this.sync);
  }

  /**
   * Track every pointer that starts inside `cluster`; `resolve` maps a live
   * client position to the set of actions it holds. Re-resolved on every
   * move, so the finger can roll/slide without lifting.
   */
  private trackCluster(cluster: HTMLDivElement, resolve: (x: number, y: number) => Set<Action>): void {
    const update = (e: PointerEvent) => {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, resolve(e.clientX, e.clientY));
      this.commit();
    };
    const end = (e: PointerEvent) => {
      if (!this.pointers.delete(e.pointerId)) return;
      this.commit();
    };
    cluster.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try {
        cluster.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone (or synthetic) — still track it below */
      }
      this.pointers.set(e.pointerId, resolve(e.clientX, e.clientY));
      this.commit();
    });
    cluster.addEventListener('pointermove', update);
    cluster.addEventListener('pointerup', end);
    cluster.addEventListener('pointercancel', end);
  }

  /** Push the union of all pointers' actions to the InputManager as a diff. */
  private commit(): void {
    const union = new Set<Action>();
    for (const set of this.pointers.values()) for (const a of set) union.add(a);
    for (const a of union) if (!this.touched.has(a)) this.input.setTouch(a, true);
    for (const a of this.touched) if (!union.has(a)) this.input.setTouch(a, false);
    this.touched = union;
  }

  /**
   * Radial d-pad: direction from the cluster centre, with a centre dead zone.
   * Both axes activate past their threshold → natural diagonals (up+left).
   */
  private dpadActions(dpad: HTMLDivElement, x: number, y: number): Set<Action> {
    const r = dpad.getBoundingClientRect();
    const dx = x - (r.left + r.width / 2);
    const dy = y - (r.top + r.height / 2);
    // Threshold ≈ half a button from centre; diagonal when both axes pass it.
    const t = r.width * 0.13;
    const out = new Set<Action>();
    if (dx < -t) out.add('left');
    if (dx > t) out.add('right');
    if (dy < -t) out.add('up');
    if (dy > t) out.add('down');
    return out;
  }

  /** Face/menu buttons: hit-test with a little slop; slide moves the press. */
  private buttonActions(cluster: HTMLDivElement, x: number, y: number): Set<Action> {
    const SLOP = 8;
    const out = new Set<Action>();
    let best: Action | null = null;
    let bestDist = Infinity;
    for (const el of Array.from(cluster.querySelectorAll<HTMLDivElement>('.tbtn'))) {
      const r = el.getBoundingClientRect();
      if (x < r.left - SLOP || x > r.right + SLOP || y < r.top - SLOP || y > r.bottom + SLOP) continue;
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
      if (d < bestDist) {
        bestDist = d;
        best = el.dataset.action as Action;
      }
    }
    if (best) out.add(best);
    return out;
  }

  // Light up buttons from the raw input state each frame, so pressing the
  // real keyboard keys (and touch presses) is reflected on the pad.
  private sync = (): void => {
    for (const b of this.buttons) b.el.classList.toggle('active', this.input.rawHeld(b.action));
    this.raf = requestAnimationFrame(this.sync);
  };

  /** Current bar height in CSS px (0 once destroyed). */
  get height(): number {
    return this.el.isConnected ? this.el.offsetHeight : 0;
  }

  static isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.pointers.clear();
    for (const a of this.touched) this.input.setTouch(a, false);
    this.touched.clear();
    this.el.remove();
    window.dispatchEvent(new Event('resize'));
  }
}
